import type { Remote } from 'codesearch-backend'
import { Show } from 'solid-js'
import { copy } from '../utils/clipboard.ts'
import { fileUrl, forgeName } from '../utils/forge.ts'
import { Button } from './ui/Button.tsx'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from './ui/Dropdown.tsx'

/*
  code search's `Links` dropdown, minus the google-internal entries. one menu instead
  of a row of icon buttons: these are all "get me a reference to this file", and they
  belong in the same place - which is also where the `l` prefix shortcuts point
*/
export function LinksMenu(props: {
  repo: string
  path: string
  line?: number
  gitRef: string
  remote: Remote | null | undefined
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onRaw: () => void
}) {
  const permalink = () => `${location.origin}${location.pathname}${location.search}`
  const remoteUrl = () => fileUrl(props.remote, { gitRef: props.gitRef, path: props.path, line: props.line })

  return (
    <DropdownMenu open={props.open} onOpenChange={props.onOpenChange}>
      <DropdownMenuTrigger
        as={Button<'button'>}
        variant="ghost"
        size="sm"
        class="h-6 shrink-0 gap-1 px-1.5 text-xs font-normal"
        title="links (l)"
      >
        <div class="i-lucide-link size-3.5 text-muted-foreground" />
        Links
        <div class="i-lucide-chevron-down size-3 text-muted-foreground" />
      </DropdownMenuTrigger>

      <DropdownMenuContent class="w-64">
        <DropdownMenuGroup>
          <DropdownMenuItem class="text-xs" onSelect={() => void copy(props.path, 'path copied')}>
            <div class="i-lucide-file size-3.5 shrink-0 text-muted-foreground" />
            Copy the file path
            <DropdownMenuShortcut>l p</DropdownMenuShortcut>
          </DropdownMenuItem>

          <DropdownMenuItem
            class="text-xs"
            onSelect={() => void copy(`${permalink()}${props.line ? `#L${props.line}` : ''}`, 'link copied')}
          >
            <div class="i-lucide-link-2 size-3.5 shrink-0 text-muted-foreground" />
            Copy a link to this line
            <DropdownMenuShortcut>l r</DropdownMenuShortcut>
          </DropdownMenuItem>
        </DropdownMenuGroup>

        <Show when={props.remote}>
          {remote => (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <Show when={remoteUrl()}>
                  {url => (
                    <DropdownMenuItem class="text-xs" onSelect={() => window.open(url(), '_blank', 'noopener')}>
                      <div class="i-lucide-external-link size-3.5 shrink-0 text-muted-foreground" />
                      <span class="truncate">
                        {`Open on ${forgeName(remote())}`}
                      </span>
                    </DropdownMenuItem>
                  )}
                </Show>

                <DropdownMenuItem class="text-xs" onSelect={() => void copy(remote().base, 'git url copied')}>
                  <div class="i-lucide-git-branch size-3.5 shrink-0 text-muted-foreground" />
                  Copy the repository url
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </>
          )}
        </Show>

        <DropdownMenuSeparator />
        <DropdownMenuItem class="text-xs" onSelect={props.onRaw}>
          <div class="i-lucide-file-code size-3.5 shrink-0 text-muted-foreground" />
          Show raw contents
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
